import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Download } from 'lucide-react';
import { Card } from '../ui/Card';
import { buildFiltersFromState } from '../../lib/filterBuilder';
import { DISPLAY_CURRENCY_OPTIONS } from '../../context/CurrencyContext';

const TIME_PERIOD_OPTIONS = [
  { label: 'All Time', value: 'all_time' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'this_week' },
  { label: 'This Month', value: 'this_month' },
  { label: 'This Year', value: 'this_year' },
  { label: 'Custom Range', value: 'custom' },
];

const PRODUCT_TYPE_OPTIONS = [
  { label: 'All Types', value: 'all' },
  { label: 'Admission', value: 'admission' },
  { label: 'Season Card', value: 'seasoncard' },
  { label: 'Added Product', value: 'added_product' },
];

function Dropdown({ label, options, value, onChange, disabled = false, badge = null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="relative" ref={ref}>
      <span className="font-display mb-2 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
        {badge && <span className="ml-1 font-sans text-[9px] text-slate-400">({badge})</span>}
      </span>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-8 w-full items-center justify-between rounded-lg border px-2.5 text-[13px] shadow-[0_2px_8px_rgba(15,23,42,0.08)] transition-colors ${
          disabled
            ? 'border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
        }`}
      >
        <span className="min-w-0 truncate pr-1">{selected.label}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${disabled ? 'text-slate-300' : 'text-slate-400'} ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && !disabled && (
        <div
          className="absolute top-full left-0 z-50 mt-1 max-h-60 min-w-full overflow-y-auto overscroll-contain rounded-lg border border-slate-200 bg-white shadow-lg"
          role="listbox"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => { onChange(option.value); setOpen(false); }}
              className={`flex w-full items-center px-3 py-2 text-[13px] text-left transition-colors hover:bg-slate-50 ${
                option.value === value ? 'font-semibold text-[#201C35]' : 'text-slate-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function buildAndEmit(state, onFiltersChange) {
  const payload = buildFiltersFromState(state);
  if (!payload) return;
  onFiltersChange?.(payload);
}

export function FilterBar({
  onFiltersChange,
  onExportCsv,
  onExportPdf,
  availableEvents = [],
  availableVenues = [],
  commitMode = false,
  committedFilters = null,
  displayCurrency = 'DKK',
  onDisplayCurrencyChange,
  ratesLoading = false,
  ratesError = null,
}) {
  const [timePeriod, setTimePeriod] = useState('all_time');
  const [venue, setVenue] = useState('all');
  const [event, setEvent] = useState('all');
  const [productType, setProductType] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const venueOptions = [
    { label: 'All Venues', value: 'all' },
    ...availableVenues.map((v) => ({ label: v.venueName, value: String(v.venueId) })),
  ];

  const filteredEvents = venue === 'all'
    ? availableEvents
    : availableEvents.filter((e) => !e.venueId || String(e.venueId) === venue);

  const eventOptions = [
    { label: 'All Events', value: 'all' },
    ...filteredEvents
      .filter((e) => {
        const name = (e.eventName || '').toLowerCase();
        return (
          e.eventName &&
          e.eventName !== 'Unknown Event' &&
          e.eventName !== 'Unknown event' &&
          !name.includes('bulktest') &&
          !name.includes('bulk test')
        );
      })
      .map((e) => ({ label: e.eventName, value: String(e.eventId) })),
  ];

  function currentState(overrides = {}) {
    return { timePeriod, venue, event, productType, customStart, customEnd, ...overrides };
  }

  // Always emit immediately — no Apply button needed
  function handleTimePeriod(val) {
    setTimePeriod(val);
    // When switching away from custom, fire immediately with preset range
    if (val !== 'custom') {
      buildAndEmit(currentState({ timePeriod: val, customStart: '', customEnd: '' }), onFiltersChange);
    }
    // When switching TO custom, wait for both dates to be filled in
  }

  function handleVenue(val) {
    setVenue(val);
    setEvent('all');
    buildAndEmit(currentState({ venue: val, event: 'all' }), onFiltersChange);
  }

  function handleEvent(val) {
    setEvent(val);
    buildAndEmit(currentState({ event: val }), onFiltersChange);
  }

  function handleProductType(val) {
    setProductType(val);
    buildAndEmit(currentState({ productType: val }), onFiltersChange);
  }

  function handleCustomStart(val) {
    setCustomStart(val);
    // Only fire when both dates are set
    if (val && customEnd) {
      buildAndEmit(currentState({ customStart: val }), onFiltersChange);
    }
  }

  function handleCustomEnd(val) {
    setCustomEnd(val);
    // Only fire when both dates are set
    if (customStart && val) {
      buildAndEmit(currentState({ customEnd: val }), onFiltersChange);
    }
  }

  function handleReset() {
    setTimePeriod('all_time');
    setVenue('all');
    setEvent('all');
    setProductType('all');
    setCustomStart('');
    setCustomEnd('');
    onFiltersChange?.({
      timePeriod: 'all_time',
      venue: 'all',
      event: 'all',
      productType: 'all',
      start: null,
      end: null,
      queryString: '',
    });
    onDisplayCurrencyChange?.('DKK');
  }

  // Sync local state when parent provides committedFilters
  useEffect(() => {
    if (!committedFilters) return;
    setTimePeriod(committedFilters.timePeriod || 'all_time');
    setVenue(committedFilters.venue || 'all');
    setEvent(committedFilters.event || 'all');
    setProductType(committedFilters.productType || 'all');
    setCustomStart(committedFilters.start || '');
    setCustomEnd(committedFilters.end || '');
  }, [committedFilters]);

  const venueDisabled = venueOptions.length <= 1;

  return (
    <Card className="min-w-0 px-3 py-2.5">
      <div className="flex min-w-0 flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex min-w-0 flex-col gap-2.5 lg:flex-row lg:items-end lg:flex-wrap">
          <div className="grid min-w-0 w-full gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
            <Dropdown
              label="Currency"
              options={DISPLAY_CURRENCY_OPTIONS}
              value={displayCurrency}
              onChange={onDisplayCurrencyChange}
              badge={ratesError ? 'Rates unavailable' : null}
            />
            <Dropdown
              label="Time Period"
              options={TIME_PERIOD_OPTIONS}
              value={timePeriod}
              onChange={handleTimePeriod}
            />
            <Dropdown
              label="Venue"
              options={venueOptions}
              value={venue}
              onChange={handleVenue}
              disabled={venueDisabled}
              badge={venueDisabled ? 'No venues yet' : null}
            />
            <Dropdown
              label="Event"
              options={eventOptions}
              value={event}
              onChange={handleEvent}
            />
            <Dropdown
              label="Product Type"
              options={PRODUCT_TYPE_OPTIONS}
              value={productType}
              onChange={handleProductType}
            />
          </div>

          {timePeriod === 'custom' && (
            <div className="mt-2 flex flex-wrap items-end gap-2 lg:mt-0">
              <div>
                <label
                  className="font-display mb-2 block text-[10px] font-semibold uppercase tracking-wide text-slate-500"
                  htmlFor="filter-start-date"
                >
                  From
                </label>
                <input
                  id="filter-start-date"
                  type="date"
                  value={customStart}
                  onChange={(e) => handleCustomStart(e.target.value)}
                  className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.08)]"
                />
              </div>
              <div>
                <label
                  className="font-display mb-2 block text-[10px] font-semibold uppercase tracking-wide text-slate-500"
                  htmlFor="filter-end-date"
                >
                  To
                </label>
                <input
                  id="filter-end-date"
                  type="date"
                  value={customEnd}
                  onChange={(e) => handleCustomEnd(e.target.value)}
                  className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.08)]"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-2 lg:w-auto">
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap px-2.5 text-[12px] text-slate-500 transition-colors hover:text-slate-700"
          >
            <X className="h-3 w-3 shrink-0" />
            Reset
          </button>
          <button
            type="button"
            onClick={onExportPdf}
            disabled={!onExportPdf}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.08)] transition duration-150 hover:scale-105 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#201C35] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
          >
            <Download className="h-3.5 w-3.5 shrink-0 text-slate-500" />
            Download PDF
          </button>
          <button
            type="button"
            onClick={onExportCsv}
            aria-label="Export CSV"
            className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.08)] transition duration-150 hover:scale-105 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#201C35]"
          >
            <Download className="h-3.5 w-3.5 shrink-0 text-slate-500" />
            Export CSV
          </button>
        </div>
      </div>
    </Card>
  );
}